// ===============================================
// 💳 СБП SERVICE (ЧЕРЕЗ БЭКЕНД API)
// ===============================================

import backendApiService from './backend-api.service';
import { sendWithCaptcha, STEAM_PAYMENT_CAPTCHA } from '../visoria/captcha';

const sbpService = {
  // ===============================================
  // 🔗 СОЗДАНИЕ QR-КОДА
  // ===============================================

  async createQRCode(payload) {
    try {
      const result = await backendApiService.createSBPQR(payload);
      return result;
    } catch (error) {
      if (error?.code === 'CAPTCHA_REQUIRED') {
        throw error;
      }
      return {
        success: false,
        message: 'Ошибка создания QR-кода СБП',
        error: error.message,
        serverError: error.serverError || null,
        code: error.code || null,
      };
    }
  },

  // ===============================================
  // СОЗДАНИЕ КРИПТОПЛАТЕЖА
  // ===============================================

  async createCryptoPayment(steamAccount, amountRUB, onProgress, consent = false, onCaptcha) {
    try {
      if (onProgress) {
        onProgress(10, 'Проверка данных...');
        await new Promise((resolve) => setTimeout(resolve, 500));

        onProgress(30, 'Создание криптоадреса...');
        await new Promise((resolve) => setTimeout(resolve, 1000));

        onProgress(70, 'Генерация QR-кода...');
        await new Promise((resolve) => setTimeout(resolve, 500));

        onProgress(90, 'Финализация...');
        await new Promise((resolve) => setTimeout(resolve, 300));
      }

      const result = await sendWithCaptcha(
        (captchaToken) =>
          backendApiService.createCryptoPayment(
            amountRUB,
            steamAccount,
            consent,
            captchaToken,
          ),
        onCaptcha,
        STEAM_PAYMENT_CAPTCHA,
      );

      if (onProgress) {
        onProgress(100, 'Криптоплатеж готов!');
      }

      return result;
    } catch (error) {
      if (error?.code === 'CAPTCHA_REQUIRED') {
        throw error;
      }
      return {
        success: false,
        message: 'Ошибка создания криптоплатежа',
        error: error.message,
      };
    }
  },

  // ===============================================
  // 🔍 ПРОВЕРКА СТАТУСА
  // ===============================================

  async checkPaymentStatus(paymentGuid) {
    try {
      return await backendApiService.checkSBPPaymentStatus(paymentGuid);
    } catch (error) {
      return {
        success: false,
        message: 'Ошибка проверки статуса платежа',
        error: error.message,
      };
    }
  },

  // ===============================================
  // 🛠️ УТИЛИТЫ
  // ===============================================

  generateVisitorId() {
    return 'visitor_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  },

  generatePaymentId() {
    return `sbp_payment_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  },

  formatAmount(amount) {
    return new Intl.NumberFormat('ru-RU', {
      style: 'currency',
      currency: 'RUB',
      minimumFractionDigits: 2,
    }).format(amount);
  },

  async getCurrencyRates() {
    try {
      const response = await fetch(`${this.baseUrl}/api/currency/rates`);
      if (response.ok) {
        const data = await response.json();
        return data.rates;
      }
      return null;
    } catch {
      return null;
    }
  },

  async isBackendAvailable() {
    try {
      await backendApiService.healthCheck();
      return true;
    } catch {
      return false;
    }
  },

  // ===============================================
  // 📊 СТАТУСЫ ПЛАТЕЖЕЙ
  // ===============================================

  getPaymentStatusText(status) {
    const statusMap = {
      SUCCESS: 'Платеж успешно завершен',
      FAILED: 'Платеж не прошел',
      PENDING: 'Ожидание оплаты',
      CANCELLED: 'Платеж отменен',
      PROCESSING: 'Платеж в обработке',
      UNKNOWN: 'Неизвестный статус',
    };

    return statusMap[status] || 'Неизвестный статус';
  },

  isPaymentSuccessful(status) {
    return status === 'SUCCESS';
  },

  isPaymentFinal(status) {
    return ['SUCCESS', 'FAILED', 'CANCELLED'].includes(status);
  },

  // ===============================================
  // 🔄 СОЗДАНИЕ ПЛАТЕЖА С ОТСЛЕЖИВАНИЕМ
  // ===============================================

  async createPaymentWithTracking(steamAccount, paymentData, onProgress, onCaptcha) {
    try {
      if (onProgress) {
        onProgress(10, 'Подготовка данных...');
        await new Promise((resolve) => setTimeout(resolve, 500));

        onProgress(30, 'Создание QR-кода...');
        await new Promise((resolve) => setTimeout(resolve, 1000));

        onProgress(60, 'Генерация ссылки...');
        await new Promise((resolve) => setTimeout(resolve, 500));

        onProgress(90, 'Финальная настройка...');
        await new Promise((resolve) => setTimeout(resolve, 300));
      }

      const basePayload = typeof paymentData === 'object' && paymentData !== null
        ? {
            steamAccount,
            steamRegion: paymentData.steamRegion || 'USD',
            amount: paymentData.amountUSD,
            amountRUB: paymentData.amountRUB,
            consent: Boolean(paymentData.consent),
          }
        : {
            steamAccount,
            amount: paymentData,
            steamRegion: 'USD',
          };

      const result = await sendWithCaptcha(
        (captchaToken) =>
          this.createQRCode(
            captchaToken ? { ...basePayload, captchaToken } : basePayload,
          ),
        onCaptcha,
        STEAM_PAYMENT_CAPTCHA,
      );

      if (result.success) {
        if (result.qrCode === 'existing_order') {
          return {
            success: true,
            qrCode: 'existing_order',
            paymentUrl: 'existing_order',
            orderId: result.orderId,
            invoiceGuid: result.invoiceGuid,
            paymentGuid: result.paymentGuid,
            delay: 0,
            message: 'Заказ уже существует',
          };
        }

        return {
          success: true,
          qrCode: result.qrCode,
          paymentUrl: result.paymentUrl,
          orderId: result.orderId,
          invoiceGuid: result.invoiceGuid,
          paymentGuid: result.paymentGuid,
          delay: 2.3,
        };
      }
      return {
        success: false,
        message: 'Ошибка создания платежа с отслеживанием',
        error: result.error || result.message || 'Ошибка создания платежа',
        serverError: result.serverError || null,
      };
    } catch (error) {
      return {
        success: false,
        message: 'Ошибка создания платежа с отслеживанием',
        error: error.message,
      };
    }
  },

  // ===============================================
  // 🔄 ПОЛЛИНГ СТАТУСА
  // ===============================================

  async pollPaymentStatus(paymentGuid, maxAttempts = 30, interval = 2000) {
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        const result = await this.checkPaymentStatus(paymentGuid);

        if (result.success && this.isPaymentFinal(result.status)) {
          return result;
        }

        if (attempt < maxAttempts) {
          await new Promise((resolve) => setTimeout(resolve, interval));
        }
      } catch (error) {
        if (attempt === maxAttempts) {
          throw error;
        }
        await new Promise((resolve) => setTimeout(resolve, interval));
      }
    }

    throw new Error('Превышено время ожидания статуса платежа');
  },
};

export default sbpService;
